import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type HTMLAttributes, type ReactElement, useState } from 'react'
import {
	ResponsiveConfirmDialog,
	ResponsiveConfirmTrigger
} from '@/features/marquee/components/responsive-confirm-dialog'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { errorHandler } from '@/helpers/utils'
import { lockEvent, type MarqueeCompletenessWarning } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface LockEventDialogProps {
	/** Event to lock via POST /events/{id}/lock. */
	eventId?: string
	/**
	 * How many invitees the lock will close as "no response"
	 * (`rsvpClosedByLockCount`). Stated in the confirmation when non-zero: the
	 * write is permanent, since the RSVP is editable in `pre_work` only and the
	 * lock cannot be undone.
	 */
	closingRsvpCount?: number
	/** Element that opens the confirmation (e.g. the "Lock event & continue" button). */
	trigger: ReactElement<HTMLAttributes<HTMLElement>>
	/**
	 * Fired once the lock lands (after cache invalidation), e.g. to continue the
	 * flow. Carries the backend's M8 completeness warning from the lock response
	 * — `null` when every non-walk-in row holds an account. This response is the
	 * only place it is ever populated, so a caller that wants it at the moment of
	 * the transition has to read it here.
	 */
	onLocked?: (warning: MarqueeCompletenessWarning | null) => void
}

/**
 * "Lock this event?" confirmation. Runs POST /events/{id}/lock on confirm,
 * closing only once the request settles.
 */
function LockEventDialog({
	eventId,
	closingRsvpCount = 0,
	trigger,
	onLocked
}: LockEventDialogProps) {
	const { t } = useTranslation()
	const [open, setOpen] = useState(false)

	const queryClient = useQueryClient()

	// Freezes the invitee set; the detail + attendees refresh once it lands.
	const lock = useMutation({
		mutationFn: async () => {
			const response = await lockEvent({
				path: { eventId: eventId ?? '' }
			})
			return response.data
		},
		onSuccess: async (event) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
			setOpen(false)
			onLocked?.(event.completeness_warning ?? null)
		},
		onError: (error) => errorHandler(error)
	})

	return (
		<ResponsiveConfirmDialog
			confirmLabel={t('marquee:lock_dialog_confirm')}
			description={
				<>
					{t('marquee:lock_dialog_description')}
					{closingRsvpCount > 0 && (
						<>
							{' '}
							{t('marquee:lock_dialog_rsvp_warning', {
								count: closingRsvpCount
							})}
						</>
					)}
				</>
			}
			isPending={lock.isPending}
			onConfirm={() => lock.mutate()}
			onOpenChange={setOpen}
			open={open}
			title={t('marquee:lock_dialog_title')}
		>
			<ResponsiveConfirmTrigger>{trigger}</ResponsiveConfirmTrigger>
		</ResponsiveConfirmDialog>
	)
}

export { LockEventDialog }
